import { NotFound } from '@/components/NotFound';
import { PageLayout } from '@/components/PageLayout';
import { PhoneNumberModal } from '@/components/PhoneNumberModal';
import { ProductDetailSkeleton } from '@/components/ProductDetailSkeleton';
import { toast } from '@/components/ToastProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBranchStore } from '@/store/branchStore';
import { useCartStore } from '@/store/cartStore';
import { useFavoritesStore } from '@/store/favoritesStore';
import { useUserStore } from '@/store/userStore';
import { Product } from '@/types/product';
import { extractIdFromSlug } from '@/utils/slug';
import { Heart, Minus, Plus } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MedPlaceholder from '../assets/medication-placeholder.webp';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel"

export default function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addItem, getItem, updateQuantity } = useCartStore();
  const { isFavorite, addFavorite, removeFavorite } = useFavoritesStore();
  const {
    currentStockDetail: product,
    stockDetailLoading: isLoading,
    stockDetailError: error,
    loadStockDetail,
  } = useBranchStore();

  const phoneNumber = useUserStore(s => s.phoneNumber);
  const setPhoneNumber = useUserStore(s => s.setPhoneNumber);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [pendingFavorite, setPendingFavorite] = useState(false);

  // Carousel API states to sync the thumbnail active indexes
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const id = slug ? extractIdFromSlug(slug) : null;

  useEffect(() => {
    if (id) {
      loadStockDetail(id);
    }
  }, [id, loadStockDetail]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Sync active thumbnail state whenever the carousel changes slides natively
  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const cartItem = product ? getItem(product.id) : undefined;
  const quantityInCart = cartItem?.quantity ?? 0;
  const availableQuantity = product?.quantity ?? 0;

  // Build safe array fallback for the carousel slides
  const images = useMemo(() => {
    if (!product) return [MedPlaceholder];
    if (product.image_urls && product.image_urls.length > 0) {
      return product.image_urls;
    }
    return [product.image_url || MedPlaceholder];
  }, [product]);

  const handleAddToCart = () => {
    if (!product) return;

    addItem({
      id: product.id,
      brand_name: product.brand_name,
      price: product.price,
      image: product.image_url || null,
      category: product.category || null,
      availableQuantity,
    });

    toast.success('Product added to cart');
  };

  const handleIncrease = () => {
    if (!product) return;
    if (quantityInCart < availableQuantity) {
      updateQuantity(product.id, quantityInCart + 1);
    }
  };

  const handleDecrease = () => {
    if (!product) return;
    updateQuantity(product.id, quantityInCart - 1);
  };

  const toggleFavorite = (providedPhone?: string) => {
    if (!product) return;

    const productForFavorites: Product = {
      ...product,
      image_urls: product.image_url ? [product.image_url] : [],
    } as unknown as Product;

    const phoneToUse = providedPhone || phoneNumber;

    if (!phoneToUse) {
      setPendingFavorite(true);
      setShowPhoneModal(true);
      return;
    }

    const favorite = isFavorite(product.id);

    if (favorite) {
      removeFavorite(product.id);
      toast.success(
        `${product.brand_name} has been removed from your favorites`
      );
    } else {
      addFavorite(productForFavorites);
      toast.success(`${product.brand_name} has been added to your favorites`);
    }
  };

  const handleToggleFavorite = () => {
    toggleFavorite();
  };

  const handlePhoneSubmit = (phone: string) => {
    setPhoneNumber(phone);
    setShowPhoneModal(false);
    if (pendingFavorite) {
      setPendingFavorite(false);
      toggleFavorite(phone);
    }
  };

  if (isLoading) {
    return (
      <PageLayout>
        <ProductDetailSkeleton />
      </PageLayout>
    );
  }

  if (error || !product) {
    return (
      <PageLayout>
        <NotFound />
      </PageLayout>
    );
  }

  const favorite = isFavorite(product.id);

  return (
    <PageLayout>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start mb-12 px-6 md:px-16 lg:px-24">

        {/* Carousel + Thumbnail Combined Column */}
        <div className="w-full flex flex-col items-center md:items-start gap-4">
          <Carousel
            setApi={setApi}
            className="w-full max-w-md relative border border-border bg-muted/20 rounded-xl overflow-hidden group"
          >
            <CarouselContent>
              {images.map((imgUrl, index) => (
                <CarouselItem key={index} className="flex items-center justify-center aspect-square">
                  <img
                    src={imgUrl}
                    alt={`${product.brand_name} view ${index + 1}`}
                    className="w-full h-full object-contain p-8 md:p-12"
                    onError={e => {
                      e.currentTarget.src = MedPlaceholder;
                    }}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>

            {images.length > 1 && (
              <>
                <CarouselPrevious className="absolute left-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-xs hover:bg-white" />
                <CarouselNext className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-xs hover:bg-white" />
              </>
            )}
          </Carousel>

          {/* Mini Thumbnail Strip */}
          {images.length > 1 && (
            <div className="flex flex-wrap gap-2.5 mt-2 justify-center md:justify-start w-full max-w-md px-1">
              {images.map((imgUrl, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => api?.scrollTo(index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 bg-muted/10 p-1 transition-all ${current === index
                      ? 'border-primary ring-2 ring-primary/10 scale-105'
                      : 'border-border hover:border-muted-foreground/40'
                    }`}
                >
                  <img
                    src={imgUrl}
                    alt={`Thumbnail preview ${index + 1}`}
                    className="w-full h-full object-contain"
                    onError={e => {
                      e.currentTarget.src = MedPlaceholder;
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Details Actions Column */}
        <div className="flex flex-col justify-center pt-2 md:pt-4">
          <div className="mb-4">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {product.category && (
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 font-medium uppercase tracking-wider">
                  {product.category}
                </Badge>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2 text-[var(--dispensary-gray-800)]">
              {product.brand_name}
            </h1>

            <p className="text-xl md:text-2xl font-bold mb-6 text-[var(--dispensary-gray-700)]">
              ₦{product.price.toLocaleString()}
            </p>

            <div className="flex items-center gap-3 max-w-md mt-5">
              {availableQuantity === 0 ? (
                <Button
                  className="flex-1 rounded-full h-10 text-xs font-semibold"
                  disabled
                >
                  Out of Stock
                </Button>
              ) : quantityInCart > 0 ? (
                <div className="flex-1 flex items-center justify-between rounded-full bg-primary h-10 px-4">
                  <button
                    type="button"
                    onClick={handleDecrease}
                    className="flex items-center justify-center rounded-full text-primary-foreground hover:bg-white/10 w-7 h-7 transition-colors"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="font-semibold text-primary-foreground text-sm">
                    {quantityInCart}
                  </span>
                  <button
                    type="button"
                    onClick={handleIncrease}
                    disabled={quantityInCart >= availableQuantity}
                    className="flex items-center justify-center rounded-full text-primary-foreground hover:bg-white/10 disabled:opacity-50 w-7 h-7 transition-colors"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              ) : (
                <Button
                  className="flex-1 rounded-full h-10 text-xs font-semibold hover:scale-[1.01] transition-transform shadow-xs"
                  onClick={handleAddToCart}
                >
                  Add to Cart
                </Button>
              )}

              <Button
                variant="outline"
                className="w-10 h-10 p-0 rounded-full hover:scale-105 transition-all bg-white shadow-xs border-muted-foreground/20"
                onClick={handleToggleFavorite}
              >
                <Heart
                  className={`h-4 w-4 transition-colors ${favorite ? 'fill-red-500 text-red-500' : 'text-muted-foreground'
                    }`}
                />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Container */}
      <Tabs defaultValue="details" className="mb-12 px-6 md:px-16 lg:px-24">
        <TabsList className="w-full justify-start border-b border-muted bg-transparent h-auto p-0 rounded-none">
          <TabsTrigger
            value="details"
            className="font-semibold text-sm pb-3 px-4 rounded-none bg-transparent data-[state=active]:bg-transparent border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary transition-all shadow-none"
          >
            Product Details
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="prose max-w-none pt-6 mt-0">
          <h4 className="mb-4 text-lg font-semibold text-[var(--dispensary-gray-800)]">
            Product Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-muted/10 p-4 rounded-xl border border-muted/50">
              <h5 className="font-semibold text-sm mb-3 text-[var(--dispensary-gray-800)] border-b pb-1.5 border-muted">
                Basic Details
              </h5>
              <ul className="space-y-2 text-xs text-[var(--dispensary-gray-700)]">
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">Brand Name:</span> <span>{product.brand_name}</span>
                </li>
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">Category:</span> <span>{product.category || 'N/A'}</span>
                </li>
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">Strength:</span> <span>{product.strength || 'N/A'}</span>
                </li>
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">Active Ingredient:</span> <span className="text-right max-w-[60%] truncate" title={product.active_ingredient || 'N/A'}>{product.active_ingredient || 'N/A'}</span>
                </li>
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">Type:</span> <span>{product.type || 'N/A'}</span>
                </li>
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">Dosage Form:</span> <span>{product.dosage_form || 'N/A'}</span>
                </li>
              </ul>
            </div>

            <div className="bg-muted/10 p-4 rounded-xl border border-muted/50">
              <h5 className="font-semibold text-sm mb-3 text-[var(--dispensary-gray-800)] border-b pb-1.5 border-muted">
                Additional Information
              </h5>
              <ul className="space-y-2 text-xs text-[var(--dispensary-gray-700)]">
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">Pack Size:</span> <span>{product.pack_size || 'N/A'}</span>
                </li>
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">Class:</span> <span>{product.class || 'N/A'}</span>
                </li>
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">Quantity Available:</span> <span>{product.quantity}</span>
                </li>
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">Sales:</span> <span>{product.sales}</span>
                </li>
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">SKU:</span> <span className="font-mono">{product.id}</span>
                </li>
                <li className="flex justify-between py-0.5">
                  <span className="font-medium text-muted-foreground">Branch:</span> <span>{product.branch.name}</span>
                </li>
              </ul>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <PhoneNumberModal
        open={showPhoneModal}
        onClose={() => setShowPhoneModal(false)}
        onSubmit={handlePhoneSubmit}
      />
    </PageLayout>
  );
}